import { useMediaQuery } from '@jouzen/ecom-components/hooks/useMediaQuery';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import {
  SwipeableProgressBar,
  SwipeableProvider,
  SwipeableSlider,
} from '@jouzen/ecom-components/v3/SwipeableSlider';
import { cx } from 'class-variance-authority';
import { useEffect, useState } from 'react';

import Motion from '@/app/components/Motion';
import SwipeableNavButtons from '@/app/components/SwipeableNavButtons';

import LandingPageMembershipTabContentCard, {
  type LandingPageMembershipTabContentCardProps,
} from './LandingPageMembershipTabContentCard';
import LandingPageMembershipTabContentFeaturedImage, {
  type LandingPageMembershipTabContentFeaturedImageProps,
} from './LandingPageMembershipTabContentFeaturedImage';

export interface LandingPageMembershipTabContentProps {
  readonly cards: LandingPageMembershipTabContentCardProps[];
  readonly featuredImage: LandingPageMembershipTabContentFeaturedImageProps;
}

const LandingPageMembershipTabContent = ({
  cards,
  featuredImage,
}: LandingPageMembershipTabContentProps) => {
  const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.large}px)`);
  const [featuredImageVisible, setFeaturedImageVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (isDesktop && !featuredImageVisible) {
      setFeaturedImageVisible(true);
    }
    if (!isDesktop && featuredImageVisible) {
      setFeaturedImageVisible(false);
    }
  }, [featuredImageVisible, isDesktop]);

  return (
    <Motion
      className="w-full"
      data-cy="landing-page-membership-tab-content"
      initial={{ opacity: 0 }}
      transition={{ duration: 0.75, type: 'tween', delay: 0.25 }}
      whileInView={{ opacity: 1 }}
    >
      <SwipeableProvider>
        <LayoutGrid>
          <GridItem
            colStart={{ sm: 'full', xxl: 'main' }}
            colEnd={{ sm: 'full', xxl: 'main' }}
            dir="ltr"
          >
            <SwipeableSlider
              className={cx(
                'scroll-ps-6 first:pl-6 last:pr-6',
                'lg:scroll-ps-[5vw] lg:first:pl-[5vw]',
                'xxl:scroll-ps-0 xxl:first:pl-0 xxl:last:pr-0',
              )}
            >
              {featuredImageVisible ? (
                <LandingPageMembershipTabContentFeaturedImage
                  {...featuredImage}
                />
              ) : null}
              {cards.map((card) => (
                <LandingPageMembershipTabContentCard
                  key={card.title}
                  {...card}
                />
              ))}
            </SwipeableSlider>
          </GridItem>
          <GridItem colStart={{ sm: 'main' }} colEnd={{ sm: 'main' }}>
            <div className="relative mt-10 lg:mt-7">
              <SwipeableProgressBar />
              <SwipeableNavButtons
                buttonClassName="lg:px-4 lg:py-5"
                className="pt-2 pb-0 lg:pt-3"
                color="sandstoneLight"
              />
            </div>
          </GridItem>
        </LayoutGrid>
      </SwipeableProvider>
    </Motion>
  );
};

export default LandingPageMembershipTabContent;
